/**
 * Caller display-name hygiene for call signaling.
 *
 * The INVITE payload's `callerDisplayName` is written by the DIALING client,
 * which falls back to the literal 'Someone' when its own profile isn't at hand
 * (car-originated dials pass no caller profile at all — the 2026-10-07
 * "Someone is calling you" field bug). The callee must treat these generic
 * fallbacks as *unresolved* and keep trying its own sources (directory cache,
 * roster lookup) instead of trusting them verbatim.
 *
 * The server (publish-voice-call-signal) also stamps the authoritative roster
 * displayName over placeholders in the relayed payload — this client-side
 * check is defense in depth for payloads from older deployments.
 *
 * Push-privacy note (#398): message pushes strip content and full names, but
 * CALL surfaces must identify the caller — call pushes carry the roster
 * displayName by design, matching the CallKit/Telecom system UI.
 */

// 'Someone' = dialing-client fallback; 'Unknown caller' / 'Unknown' =
// callee-side resolution fallbacks; 'Call in progress' = the neutral label the
// rehydrate / CallKit-snapshot seeds use when no name is at hand. All of them
// mean "identity not resolved yet" — surfaces reading session.peer.displayName
// (return-to-call pill, call overlay, ongoing notification) may upgrade them.
const PLACEHOLDER_CALLER_NAMES = new Set([
  'someone',
  'unknown caller',
  'unknown',
  'call in progress',
])

/** True when a caller name is empty or a known generic fallback label. */
export function isPlaceholderCallerName(name: string | null | undefined): boolean {
  const trimmed = (name ?? '').trim().toLowerCase()
  return trimmed === '' || PLACEHOLDER_CALLER_NAMES.has(trimmed)
}
